import React, { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import defaultArt from '../assets/images/dj_kip_art.png';
import LoadMoreBtn from '../components/LoadMoreBtn';
import LoadingScreen from '../components/LoadingScreen';
import { useApi } from '../context/ApiProvider';
import { Typography, Box, FormControl, Select, MenuItem } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { genreOptions } from '../constants/listFilterOptions';
import SetsTable from '../components/SetsTable';
import TracksTable from '../components/TracksTable';
import HorizontalSection from '../components/sections/HorizontalSection';
import ArtistCard from '../components/ArtistCard';
import LabelCard from '../components/LabelCard';

/**
 * Genres Component
 *
 * This component displays genre-specific content including:
 * - Featured artists for the selected genre
 * - Featured labels for the selected genre
 * - Paginated sets table with load more functionality
 * - Paginated tracks table with load more functionality
 *
 * Features:
 * - Consistent pagination patterns for sets and tracks
 * - Individual loading states for each section
 * - Proper error handling with fallback states
 * - Genre filtering with URL query parameter support
 * - Separate load more functionality for sets and tracks
 */
const Genres = () => {
  const [searchParams] = useSearchParams();
  const [genreQueryParam, setGenreQueryParam] = useState(
    searchParams.get('genre') || 'House'
  );
  const [selectedGenre, setSelectedGenre] = useState(
    searchParams.get('genre') || 'House'
  );

  // Data states
  const [sets, setSets] = useState([]);
  const [artists, setArtists] = useState([]);
  const [labels, setLabels] = useState([]);
  const [tracks, setTracks] = useState([]);

  // Pagination states
  const [setsPagination, setSetsPagination] = useState({});
  const [tracksPagination, setTracksPagination] = useState({});

  // Loading states
  const [loadingSets, setLoadingSets] = useState(false);
  const [loadingArtists, setLoadingArtists] = useState(false);
  const [loadingLabels, setLoadingLabels] = useState(false);
  const [loadingTracks, setLoadingTracks] = useState(false);
  const [loadingMoreSets, setLoadingMoreSets] = useState(false);
  const [loadingMoreTracks, setLoadingMoreTracks] = useState(false);

  // Page counters
  const [setsPage, setSetsPage] = useState(1);
  const [tracksPage, setTracksPage] = useState(1);

  // Pagination configs
  const [setsPaginationConfig, setSetsPaginationConfig] = useState({
    order_by: 'set_meta.release_date',
    order_direction: 'desc',
    search: '',
    genre: searchParams.get('genre') || 'House',
    followed_filter: '',
  });

  const [tracksPaginationConfig, setTracksPaginationConfig] = useState({
    order_by: 'track_meta.release_date',
    order_direction: 'desc',
    search: '',
    genre: searchParams.get('genre') || 'House',
  });

  const api = useApi();

  useEffect(() => {
    if (genreQueryParam) {
      setSetsPaginationConfig({
        ...setsPaginationConfig,
        genre: genreQueryParam,
      });
      setTracksPaginationConfig({
        ...tracksPaginationConfig,
        genre: genreQueryParam,
      });
      setSelectedGenre(genreQueryParam);
    }
  }, [genreQueryParam]);

  useEffect(() => {
    if (selectedGenre) {
      loadArtists();
      loadLabels();
    }
  }, [selectedGenre]);

  useEffect(() => {
    loadSets();
  }, [setsPaginationConfig]);

  useEffect(() => {
    loadTracks();
  }, [tracksPaginationConfig]);

  const loadSets = async () => {
    if (loadingSets) return;
    setLoadingSets(true);

    try {
      const response = await api.get('/sets', setsPaginationConfig);
      if (response.ok) {
        setSets(response.body.data);
        setSetsPagination(response.body.pagination || {});
        setSetsPage(1);
        // Only clear genreQueryParam if it's not from URL search params
        if (!searchParams.get('genre')) {
          setGenreQueryParam(null);
        }
      } else {
        console.error('Failed to load sets:', response);
        setSets([]);
        setSetsPagination({});
      }
    } catch (error) {
      console.error('Error loading sets:', error);
      setSets([]);
      setSetsPagination({});
    } finally {
      setLoadingSets(false);
    }
  };

  const loadArtists = async () => {
    if (loadingArtists) return;
    setLoadingArtists(true);

    try {
      const response = await api.get('/artists/top-by-genre', {
        genre: selectedGenre,
      });
      if (response.ok) {
        const artistsData = response.body.data || response.body;
        setArtists(artistsData);
      } else {
        console.error('Failed to load artists:', response);
        setArtists([]);
      }
    } catch (error) {
      console.error('Error loading artists:', error);
      setArtists([]);
    } finally {
      setLoadingArtists(false);
    }
  };

  const loadLabels = async () => {
    if (loadingLabels) return;
    setLoadingLabels(true);

    try {
      const response = await api.get('/labels/top-by-genre', {
        genre: selectedGenre,
      });
      if (response.ok) {
        const labelsData = response.body.data || response.body;
        setLabels(labelsData);
      } else {
        console.error('Failed to load labels:', response);
        setLabels([]);
      }
    } catch (error) {
      console.error('Error loading labels:', error);
      setLabels([]);
    } finally {
      setLoadingLabels(false);
    }
  };

  const loadTracks = async () => {
    if (loadingTracks) return;
    setLoadingTracks(true);

    try {
      const response = await api.get('/tracks', tracksPaginationConfig);
      if (response.ok) {
        const tracksData = response.body.data || response.body;
        setTracks(tracksData);
        setTracksPagination(response.body.pagination || {});
        setTracksPage(1);
      } else {
        console.error('Failed to load tracks:', response);
        setTracks([]);
        setTracksPagination({});
      }
    } catch (error) {
      console.error('Error loading tracks:', error);
      setTracks([]);
      setTracksPagination({});
    } finally {
      setLoadingTracks(false);
    }
  };

  const loadMoreSets = async () => {
    setLoadingMoreSets(true);

    try {
      const response = await api.get('/sets', {
        ...setsPaginationConfig,
        offset: 20 * setsPage,
      });
      if (response.ok) {
        setSets([...sets, ...response.body.data]);
        setSetsPagination(response.body.pagination || {});
        setSetsPage(setsPage + 1);
      } else {
        console.error('Failed to load more sets:', response);
      }
    } catch (error) {
      console.error('Error loading more sets:', error);
    } finally {
      setLoadingMoreSets(false);
    }
  };

  const loadMoreTracks = async () => {
    setLoadingMoreTracks(true);

    try {
      const response = await api.get('/tracks', {
        ...tracksPaginationConfig,
        offset: 20 * tracksPage,
      });
      if (response.ok) {
        setTracks([...tracks, ...response.body.data]);
        setTracksPagination(response.body.pagination || {});
        setTracksPage(tracksPage + 1);
      } else {
        console.error('Failed to load more tracks:', response);
      }
    } catch (error) {
      console.error('Error loading more tracks:', error);
    } finally {
      setLoadingMoreTracks(false);
    }
  };

  function onFilter(genre, sortBy, sortDirection, followedFilter = false) {
    if (genre === '' && genreQueryParam) {
      setGenreQueryParam('');
    }

    const newGenre = genre || selectedGenre;
    setSelectedGenre(newGenre);
    setSetsPaginationConfig({
      ...setsPaginationConfig,
      genre: newGenre,
      order_direction: sortDirection,
      order_by: `set_meta.${sortBy}`,
      followed_filter: followedFilter ? 'true' : '',
    });
  }

  const handleGenreChange = (event) => {
    const newGenre = event.target.value;
    setSelectedGenre(newGenre);
    setSetsPaginationConfig({
      ...setsPaginationConfig,
      genre: newGenre,
    });
    setTracksPaginationConfig({
      ...tracksPaginationConfig,
      genre: newGenre,
    });
  };

  return (
    <>
      <PageHeader backgroundArt={defaultArt} minHeight={350}>
        <Typography variant="h3" fontWeight="bold" mt={4}>
          Explore{' '}
          <Typography
            variant="h3"
            component="span"
            color="primary"
            fontWeight="bold"
          >
            {selectedGenre}
          </Typography>
        </Typography>
        <Typography mt={2}>
          Discover the best music in the {selectedGenre} genre
        </Typography>

        {/* Genre Dropdown */}
        <Box mt={3}>
          <FormControl sx={{ minWidth: 200 }}>
            <Select
              value={selectedGenre}
              onChange={handleGenreChange}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '& .MuiSvgIcon-root': {
                  color: 'white',
                },
              }}
            >
              {genreOptions.map((genre) => (
                <MenuItem key={genre} value={genre}>
                  {genre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </PageHeader>

      <Box sx={{ padding: { xs: '20px', lg: '30px 0' }, textAlign: 'center' }}>
        <Box my={2}>
          {/* Featured Artists Section */}
          <Typography variant="h5" color="white" textAlign="left" mb={2}>
            Featured {selectedGenre} Artists
          </Typography>
          <Box mb={4}>
            <HorizontalSection
              cardComponent={ArtistCard}
              items={artists}
              loading={loadingArtists}
            />
          </Box>

          {/* Featured Labels Section */}
          <Typography variant="h5" color="white" textAlign="left" mb={2}>
            Featured {selectedGenre} Labels
          </Typography>
          <Box mb={4}>
            <HorizontalSection
              cardComponent={LabelCard}
              items={labels}
              loading={loadingLabels}
            />
          </Box>

          {/* Sets Table */}
          <Typography variant="h5" color="white" textAlign="left" mb={2}>
            Newest {selectedGenre} Sets
          </Typography>
          {loadingSets ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="white">Loading sets...</Typography>
            </Box>
          ) : (
            <>
              <SetsTable
                items={sets}
                onTableInteraction={setSetsPaginationConfig}
                tableInteractionConfig={setsPaginationConfig}
                showGenreLink={false}
              />
              <LoadMoreBtn
                pagination={setsPagination}
                onLoadMoreClick={loadMoreSets}
                loading={loadingMoreSets}
              />
            </>
          )}

          {/* Tracks Table */}
          <Typography variant="h5" color="white" textAlign="left" mb={2} mt={4}>
            Newest {selectedGenre} Tracks
          </Typography>
          {loadingTracks ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="white">Loading tracks...</Typography>
            </Box>
          ) : (
            <>
              <TracksTable items={tracks} showGenreLink={false} />
              <LoadMoreBtn
                pagination={tracksPagination}
                onLoadMoreClick={loadMoreTracks}
                loading={loadingMoreTracks}
              />
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default Genres;
